import type { Debt, RecoveryAnchor } from '@/types'

/**
 * Pure recovery-progress math. Lives in the engine so it stays React-free,
 * unit-testable, and easy to reuse from non-Dashboard surfaces (e.g. future
 * Path-to-Freedom milestone unlocks, share-card generation).
 *
 *   recoveryProgress = (startingTotal - currentOptimizedTotal) / startingTotal
 *
 * "Optimized" means `isInOptimizationEngine === true && balance > 0` —
 * matches how the rest of the app counts portfolio totals so the headline
 * doesn't disagree with the SummaryCards. Mortgages / static debts excluded
 * from the engine are also excluded from the recovery percentage.
 */

export interface RecoveryProgress {
  /** Optimized debt total at anchor's startDate (snapshotted by the user). */
  startingTotal: number
  /** Current optimized debt total. */
  currentTotal: number
  /** Amount paid down since startDate (`startingTotal - currentTotal`). */
  paidDown: number
  /** Progress fraction in [0, 1]. Clamped — see notes below. */
  fraction: number
  /** `fraction * 100`, rounded to one decimal for display. */
  percent: number
  /**
   * - `'paying-down'` — current < starting (the happy path).
   * - `'gained'` — current > starting (debt has grown since anchor).
   * - `'flat'` — current ≈ starting (within 0.5% of the anchor).
   * - `'cleared'` — current is essentially 0 (debt-free, all-time celebration).
   * - `'no-anchor'` — anchor is null (caller should show empty/init state).
   */
  state: 'paying-down' | 'gained' | 'flat' | 'cleared' | 'no-anchor'
}

const FLAT_TOLERANCE = 0.005 // 0.5%
const CLEARED_THRESHOLD = 1 // <$1 remaining is functionally zero

/**
 * Sum the balances of debts that count toward recovery progress — the same
 * filter used everywhere else for "optimized portfolio."
 */
export function totalOptimizedBalance(debts: Debt[]): number {
  return debts
    .filter((d) => d.isInOptimizationEngine && d.balance > 0)
    .reduce((sum, d) => sum + d.balance, 0)
}

/**
 * Compute the user's recovery progress against their anchor.
 *
 * Edge cases worth knowing:
 * - **No anchor**: returns `state: 'no-anchor'` and zeroed numbers. The UI
 *   should either auto-init via `useRecoveryAnchor` or render an empty state.
 * - **Anchor with `startingTotal <= 0`**: nonsensical input (user typed 0 by
 *   accident). Returns `fraction: 0` so we don't divide by zero, with state
 *   set to `'flat'` — the UI can prompt the user to fix the anchor.
 * - **`current > starting`** (debt grew): we report the *negative* fraction so
 *   the UI can surface "+$X since start" honestly. Tone matters here — see
 *   RecoveryProgressCard for how this gets framed.
 * - **`current <= $1`**: `'cleared'`. Reserve the strongest celebration.
 */
export function computeRecoveryProgress(
  anchor: RecoveryAnchor | null,
  debts: Debt[]
): RecoveryProgress {
  if (!anchor) {
    return {
      startingTotal: 0,
      currentTotal: 0,
      paidDown: 0,
      fraction: 0,
      percent: 0,
      state: 'no-anchor',
    }
  }

  const startingTotal = anchor.startingTotal
  const currentTotal = totalOptimizedBalance(debts)
  const paidDown = startingTotal - currentTotal

  if (startingTotal <= 0) {
    return {
      startingTotal,
      currentTotal,
      paidDown,
      fraction: 0,
      percent: 0,
      state: 'flat',
    }
  }

  if (currentTotal < CLEARED_THRESHOLD) {
    return {
      startingTotal,
      currentTotal,
      paidDown,
      fraction: 1,
      percent: 100,
      state: 'cleared',
    }
  }

  const rawFraction = paidDown / startingTotal
  const absFraction = Math.abs(rawFraction)
  const state: RecoveryProgress['state'] =
    absFraction < FLAT_TOLERANCE ? 'flat' : rawFraction < 0 ? 'gained' : 'paying-down'

  return {
    startingTotal,
    currentTotal,
    paidDown,
    fraction: rawFraction,
    percent: Math.round(rawFraction * 1000) / 10,
    state,
  }
}
